/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PaginatedProcessedNotificationListList } from '../models/PaginatedProcessedNotificationListList'
import type { PaginatedWaitingNotificationFullList } from '../models/PaginatedWaitingNotificationFullList'
import type { PatchedWaitingNotificationFull } from '../models/PatchedWaitingNotificationFull'
import type { PhoneNumber } from '../models/PhoneNumber'
import type { ProcessedNotificationFull } from '../models/ProcessedNotificationFull'
import type { ProcessedNotificationList } from '../models/ProcessedNotificationList'
import type { SQLReadNotification } from '../models/SQLReadNotification'
import type { Success } from '../models/Success'
import type { UnreadCount } from '../models/UnreadCount'
import type { UpsertWaitingNotifications } from '../models/UpsertWaitingNotifications'
import type { WaitingNotificationFull } from '../models/WaitingNotificationFull'
import type { WaitingNotificationsBulk } from '../models/WaitingNotificationsBulk'

import type { CancelablePromise } from '../core/CancelablePromise'
import { OpenAPI } from '../core/OpenAPI'
import { request as __request } from '../core/request'

export class NotificationService {
  /**
   * @returns Success
   * @throws ApiError
   */
  public static notificationCountRetrieve(): CancelablePromise<Success> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/count/'
    })
  }

  /**
   * @param requestBody
   * @returns PhoneNumber
   * @throws ApiError
   */
  public static notificationDeleteUserCreate(
    requestBody: PhoneNumber
  ): CancelablePromise<PhoneNumber> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/notification/delete-user/',
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param categories
   * @param categoryPrefix
   * @param deliveryMethod The method used for delivering the notification (e.g., email, SMS).
   *
   * * `sms` - sms
   * * `email` - email
   * * `push` - push
   * * `internal` - internal
   * * `socket` - socket
   * @param email recipient as email
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search
   * @param status The current status of the notification delivery (e.g., 'WAITING', 'SENT').
   *
   * * `waiting` - waiting
   * * `queued` - queued
   * * `distributed` - distributed
   * * `failed` - failed
   * * `cancelled` - cancelled
   * @param user recipient as user sso_id
   * @returns PaginatedProcessedNotificationListList
   * @throws ApiError
   */
  public static notificationItemsList(
    categories?: string,
    categoryPrefix?: string,
    deliveryMethod?: 'email' | 'internal' | 'push' | 'sms' | 'socket',
    email?: string,
    ordering?: string,
    page?: number,
    perPage?: number,
    search?: string,
    status?: Array<
      'cancelled' | 'distributed' | 'failed' | 'queued' | 'waiting'
    >,
    user?: string
  ): CancelablePromise<PaginatedProcessedNotificationListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/items/',
      query: {
        categories: categories,
        category_prefix: categoryPrefix,
        delivery_method: deliveryMethod,
        email: email,
        ordering: ordering,
        page: page,
        per_page: perPage,
        search: search,
        status: status,
        user: user
      }
    })
  }

  /**
   * @param id A unique integer value identifying this processed sql notification.
   * @returns ProcessedNotificationFull
   * @throws ApiError
   */
  public static notificationItemsRetrieve(
    id: number
  ): CancelablePromise<ProcessedNotificationFull> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/items/{id}/',
      path: {
        id: id
      }
    })
  }

  /**
   * @param id A unique integer value identifying this processed sql notification.
   * @param requestBody
   * @returns ProcessedNotificationList
   * @throws ApiError
   */
  public static notificationItemsUpdate(
    id: number,
    requestBody: ProcessedNotificationList
  ): CancelablePromise<ProcessedNotificationList> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/notification/items/{id}/',
      path: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param id A unique integer value identifying this processed sql notification.
   * @returns void
   * @throws ApiError
   */
  public static notificationItemsDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/notification/items/{id}/',
      path: {
        id: id
      }
    })
  }

  /**
   * @returns ProcessedNotificationList
   * @throws ApiError
   */
  public static notificationItemsPrefixesRetrieve(): CancelablePromise<ProcessedNotificationList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/items/prefixes/'
    })
  }

  /**
   * @param categories
   * @param categoryPrefix
   * @param deliveryMethod The method used for delivering the notification (e.g., email, SMS).
   *
   * * `sms` - sms
   * * `email` - email
   * * `push` - push
   * * `internal` - internal
   * * `socket` - socket
   * @param email recipient as email
   * @param search
   * @param status The current status of the notification delivery (e.g., 'WAITING', 'SENT').
   *
   * * `waiting` - waiting
   * * `queued` - queued
   * * `distributed` - distributed
   * * `failed` - failed
   * * `cancelled` - cancelled
   * @param user recipient as user sso_id
   * @returns UnreadCount
   * @throws ApiError
   */
  public static notificationItemsUnreadCountRetrieve(
    categories?: string,
    categoryPrefix?: string,
    deliveryMethod?: 'email' | 'internal' | 'push' | 'sms' | 'socket',
    email?: string,
    search?: string,
    status?: Array<
      'cancelled' | 'distributed' | 'failed' | 'queued' | 'waiting'
    >,
    user?: string
  ): CancelablePromise<UnreadCount> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/items/unread-count/',
      query: {
        categories: categories,
        category_prefix: categoryPrefix,
        delivery_method: deliveryMethod,
        email: email,
        search: search,
        status: status,
        user: user
      }
    })
  }

  /**
   * @param requestBody
   * @returns SQLReadNotification
   * @throws ApiError
   */
  public static notificationReadCreate(
    requestBody: SQLReadNotification
  ): CancelablePromise<SQLReadNotification> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/notification/read/',
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param requestBody
   * @returns UpsertWaitingNotifications
   * @throws ApiError
   */
  public static notificationUpsertCreate(
    requestBody: UpsertWaitingNotifications
  ): CancelablePromise<UpsertWaitingNotifications> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/notification/upsert/',
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @returns PaginatedWaitingNotificationFullList
   * @throws ApiError
   */
  public static notificationWaitingList(
    page?: number,
    perPage?: number
  ): CancelablePromise<PaginatedWaitingNotificationFullList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/waiting/',
      query: {
        page: page,
        per_page: perPage
      }
    })
  }

  /**
   * @param requestBody
   * @returns WaitingNotificationFull
   * @throws ApiError
   */
  public static notificationWaitingCreate(
    requestBody: WaitingNotificationFull
  ): CancelablePromise<WaitingNotificationFull> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/notification/waiting/',
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param id A unique integer value identifying this waiting sql notification.
   * @returns WaitingNotificationFull
   * @throws ApiError
   */
  public static notificationWaitingRetrieve(
    id: number
  ): CancelablePromise<WaitingNotificationFull> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/notification/waiting/{id}/',
      path: {
        id: id
      }
    })
  }

  /**
   * @param id A unique integer value identifying this waiting sql notification.
   * @param requestBody
   * @returns WaitingNotificationFull
   * @throws ApiError
   */
  public static notificationWaitingUpdate(
    id: number,
    requestBody: WaitingNotificationFull
  ): CancelablePromise<WaitingNotificationFull> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/notification/waiting/{id}/',
      path: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param id A unique integer value identifying this waiting sql notification.
   * @param requestBody
   * @returns WaitingNotificationFull
   * @throws ApiError
   */
  public static notificationWaitingPartialUpdate(
    id: number,
    requestBody?: PatchedWaitingNotificationFull
  ): CancelablePromise<WaitingNotificationFull> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/notification/waiting/{id}/',
      path: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param id A unique integer value identifying this waiting sql notification.
   * @returns void
   * @throws ApiError
   */
  public static notificationWaitingDestroy(
    id: number
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/notification/waiting/{id}/',
      path: {
        id: id
      }
    })
  }

  /**
   * @param requestBody
   * @returns WaitingNotificationsBulk
   * @throws ApiError
   */
  public static notificationWaitingBulkCreate(
    requestBody: WaitingNotificationsBulk
  ): CancelablePromise<WaitingNotificationsBulk> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/notification/waiting/bulk/',
      body: requestBody,
      mediaType: 'application/json'
    })
  }
}
